/**
 * Full-page invoice/estimate chrome (header, patient selector, Invoice/BatchInvoice).
 * Use for routes such as `/estimate/new` (create) or `/estimates/:estimateId` (existing).
 * Behavior is aligned with InvoiceDialog; keep changes in sync when both should match.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibContainer, KibGrid, KibGridItem } from '@chewy/kib-layout-react'
import {
  AlertIconType,
  type Nil,
  type Patient,
  Utils,
} from '@pbt/pbt-ui-components'

import ClientWarningChip from '~/components/common/ClientWarningChip'
import BusinessShareIcon from '~/components/common/icons/BusinessShareIcon'
import ClientInfoLabel from '~/components/common/labels/ClientInfoLabel'
import PatientInfoLabel from '~/components/common/labels/PatientInfoLabel'
import PatientWarningChip from '~/components/common/PatientWarningChip'
import { PatientPreferencesChip } from '~/components/dashboard/clients/patient/PatientPreferencesChip'
import DialogNames from '~/constants/DialogNames'
import { OrderType } from '~/constants/SOAPStates'
import {
  clearCurrentInvoiceData,
  fetchEstimateTemplate,
  fetchInvoice,
  fetchInvoiceByEventId,
  fetchInvoiceTemplate,
} from '~/store/actions/finance'
import { refreshOrders } from '~/store/actions/orders'
import { fetchModality } from '~/store/duck/imagingOrders'
import {
  useGetInvoiceLogsByLogType,
  useHasUnfinishedLabs,
} from '~/store/hooks/finance'
import { getCurrentBusinessId } from '~/store/reducers/auth'
import {
  getFinanceInvoice,
  getFinanceInvoiceByEventId,
  getFinanceIsFetching,
  getFinanceTemplate,
} from '~/store/reducers/finance'
import { getPatient } from '~/store/reducers/patients'
import { getUser } from '~/store/reducers/users'
import type { InvoiceOrEstimate, Prescription } from '~/types'
import { getIsBatchInvoice } from '~/utils/invoiceUtils'
import useDialog from '~/utils/useDialog'

import { ClientPreferencesChip } from '../clients/preferences/ClientPreferencesChip'
import LinkedChewyAccountContainer, {
  KyriosAccountType,
} from '../link-chewy-account/LinkedChewyAccountContainer'
import WellnessPlanMemberButton from '../wellness-plans/WellnessPlanMemberButton'
import BatchInvoice, { type BatchInvoiceProps } from './batch/BatchInvoice'
import Invoice, { type InvoiceHandle, type InvoiceProps } from './Invoice'
import {
  formatInvoiceHeaderSubtitle,
  getInvoiceNumberSuffix,
  getInvoiceTypeDisplayLabel,
} from './invoiceBody/invoiceBodyDisplayUtils'
import {
  computeFetchInvoiceByEventId,
  shouldRefetchEstimateWhenWrongInvoiceTemplate,
} from './invoiceBody/invoiceBodyRefetchGuards'
import { useResetSelectedPatientWhenClosed } from './invoiceBody/useResetSelectedPatientWhenClosed'
import { useSyncEstimateFromInvoiceState } from './invoiceBody/useSyncEstimateFromInvoiceState'
import PatientSelector from './PatientSelector'

import '@chewy/kib-layout-styles/dist/styles/kib-grid.css'
import styles from './InvoiceBody.module.scss'

export type InvoiceBodyHandle = {
  attemptClose: (redirectTo: string) => void
  getHasUnsavedChanges: () => boolean
  save: () => void
}

export type InvoiceBodyProps = {
  clientId?: string | Nil
  create?: boolean
  eventId?: string | Nil
  fromTimeline?: boolean
  invoiceId?: string | Nil
  isEstimate?: boolean
  isOtcInvoice?: boolean
  newEstimateFlow?: boolean
  onClose?: (redirectTo?: string) => void
  open: boolean
  patientId?: string | Nil
  prescriptionId?: string
  refillItem?: Prescription
  setAttachingToSoapEstimateId?: (attachingToSoapEstimateId: string) => void
  setSoapToAttachEstimateId?: (soapToAttachEstimateId: string) => void
  soapBusinessId?: string | Nil
  soapId?: string | Nil
}

const InvoiceBody = forwardRef<InvoiceBodyHandle, InvoiceBodyProps>(
  function InvoiceBody(
    {
      isEstimate: isEstimateProp,
      isOtcInvoice,
      eventId,
      invoiceId,
      open,
      onClose,
      clientId,
      patientId,
      soapBusinessId,
      soapId,
      create,
      prescriptionId,
      refillItem,
      fromTimeline,
      newEstimateFlow,
      setAttachingToSoapEstimateId,
      setSoapToAttachEstimateId,
    },
    ref,
  ) {
    const dispatch = useDispatch()
    const template = useSelector(getFinanceTemplate)
    const isFetching = useSelector(getFinanceIsFetching)
    const clientFromStore = useSelector(getUser(clientId))
    const patientFromStore = useSelector(getPatient(patientId))
    const currentBusinessId = useSelector(getCurrentBusinessId)
    const { t } = useTranslation(['Common', 'Dialogs'])

    const UNFINISHED_LABS_MESSAGE = [
      t('Dialogs:INVOICE_DIALOG.UNFINISHED_LABS_MESSAGE_01'),
      t('Dialogs:INVOICE_DIALOG.UNFINISHED_LABS_MESSAGE_02'),
    ].join('\n\n')

    const fetchByEventId = computeFetchInvoiceByEventId({
      create,
      eventId,
      invoiceId,
      isFetching,
      open,
    })
    const invoiceFromState = useSelector(
      fetchByEventId && eventId
        ? getFinanceInvoiceByEventId(eventId, clientId)
        : getFinanceInvoice(invoiceId || template?.id),
    )
    const invoicePatient = useSelector(getPatient(invoiceFromState?.patientId))
    const invoiceClient = useSelector(getUser(invoiceFromState?.clientId))

    const [selectedPatient, setSelectedPatient] = useState(patientFromStore)
    const [isEstimate, setIsEstimate] = useState(Boolean(isEstimateProp))
    const [openAddItem, setOpenAddItem] = useState(!isEstimate && create)

    const invoiceRef = useRef<InvoiceHandle>(null)

    const invoice = invoiceFromState || template || ({} as InvoiceOrEstimate)
    const showPatientSelector = create && !selectedPatient
    const invoiceNoSuffix = getInvoiceNumberSuffix(invoice)
    const invoiceType = getInvoiceTypeDisplayLabel(isEstimate, invoice)

    const patient = invoicePatient || patientFromStore
    const client = invoiceClient || clientFromStore
    const patientName = patient?.name
    const clientName = Utils.getPersonString(client)

    const labLogs = useGetInvoiceLogsByLogType(OrderType.LAB_TEST, invoice)
    const hasUnfinishedLabs = useHasUnfinishedLabs(labLogs)
    const isPosted = invoice.posted

    const [openAddInvoiceItemDialog] = useDialog(
      DialogNames.ADD_INVOICE_ITEM,
      null,
      {
        invoice,
        isEstimate,
        isOtcInvoice,
      },
    )
    const [openConfirmActionDialog, closeConfirmActionDialog] = useDialog(
      DialogNames.DISMISSIBLE_ALERT,
    )

    useSyncEstimateFromInvoiceState(invoiceFromState, setIsEstimate)

    useResetSelectedPatientWhenClosed(open, setSelectedPatient)

    useEffect(() => {
      if (currentBusinessId) {
        dispatch(fetchModality(currentBusinessId))
      }
    }, [currentBusinessId])

    const hasOrderFilters = Boolean(invoice.orderFilters)

    useEffect(() => {
      if (!isEstimate && !refillItem && create && hasOrderFilters) {
        openAddInvoiceItemDialog({
          invoice,
          isEstimate,
          isOtcInvoice,
        })
      }
    }, [create, isEstimate, hasOrderFilters])

    useEffect(() => {
      if (open && create && selectedPatient) {
        if (isEstimate) {
          dispatch(fetchEstimateTemplate(clientId, selectedPatient.id))
          return
        }
        dispatch(fetchInvoiceTemplate(clientId, selectedPatient.id, eventId))
      }
    }, [open, selectedPatient, isEstimate])

    const refetchInvoice = () => {
      if (fetchByEventId && eventId) {
        dispatch(fetchInvoiceByEventId(clientId, eventId))
      } else if (!create && invoiceId && open && !isFetching) {
        dispatch(fetchInvoice(invoiceId))
      }
    }

    useEffect(() => {
      refetchInvoice()
    }, [open, eventId, invoiceId])

    /*
  Need to refetch estimate template in case of template was reassigned to
  INVOICE template with fetchInvoiceByEventId (e.g. in timetable#notifyAutochargedSoap)
  */
    const refetchEstimateOnIncorrectTemplateType =
      shouldRefetchEstimateWhenWrongInvoiceTemplate(
        isEstimate,
        create,
        invoice,
        selectedPatient,
      )

    useEffect(() => {
      if (refetchEstimateOnIncorrectTemplateType && selectedPatient) {
        dispatch(fetchEstimateTemplate(clientId, selectedPatient.id))
      }
    }, [refetchEstimateOnIncorrectTemplateType])

    const handleClose = useCallback(
      (redirectTo: string) => {
        setOpenAddItem(false)
        dispatch(clearCurrentInvoiceData())
        dispatch(refreshOrders())
        onClose?.(redirectTo)
      },
      [dispatch, onClose],
    )

    const attemptClose = useCallback(
      (redirectTo: string) => {
        if (!isEstimate && hasUnfinishedLabs && !isPosted) {
          openConfirmActionDialog({
            cancelButtonText: t('Common:LEAVE_ANYWAY_ACTION'),
            classes: {
              message: styles.confirmationMessage,
            },
            iconType: AlertIconType.WARN,
            message: UNFINISHED_LABS_MESSAGE,
            okButtonText: t('Dialogs:INVOICE_DIALOG.OK_BUTTON_TEXT'),
            onCancel: () => {
              closeConfirmActionDialog()
              handleClose(redirectTo)
            },
            onClose: closeConfirmActionDialog,
            onOk: closeConfirmActionDialog,
          })
        } else {
          if (invoiceRef?.current?.handleClearSearch) {
            invoiceRef.current.handleClearSearch()
          }
          handleClose(redirectTo)
        }
      },
      [
        styles.confirmationMessage,
        closeConfirmActionDialog,
        handleClose,
        hasUnfinishedLabs,
        isEstimate,
        isPosted,
        openConfirmActionDialog,
        t,
        UNFINISHED_LABS_MESSAGE,
      ],
    )

    useImperativeHandle(
      ref,
      () => ({
        attemptClose,
        getHasUnsavedChanges: () =>
          Boolean(invoiceRef.current?.hasUnsavedChanges()),
        save: () => {
          invoiceRef.current?.save()
        },
      }),
      [attemptClose],
    )

    const onPatientSelected = (newPatient: Patient) => {
      setSelectedPatient(newPatient)
      setOpenAddItem(true)
    }

    const isBatchInvoice = getIsBatchInvoice(invoice)
    const invoiceProps = {
      clientId,
      create,
      eventId,
      fromTimeline,
      hideControls: invoiceId && !invoiceFromState,
      invoice,
      isEstimate,
      isNew:
        !invoiceId &&
        (!isOtcInvoice || !invoice.id) &&
        Boolean(selectedPatient),
      isOtcInvoice,
      onClose,
      openAddItem,
      patient: patientFromStore,
      prescriptionLineItemId: prescriptionId,
      ref: invoiceRef,
      refetchInvoice,
      refillItem,
      soapBusinessId,
      soapId,
      onOk: attemptClose,
      invoiceIds: isBatchInvoice ? invoice.invoices : [invoice.id],
      id: invoiceFromState?.id,
      newEstimateFlow,
      setAttachingToSoapEstimateId,
      setSoapToAttachEstimateId,
    } as unknown as BatchInvoiceProps | InvoiceProps

    return (
      <KibContainer className={styles.root}>
        <div className={styles.header}>
          {(!invoiceId || invoiceFromState) && (
            <h2 className={styles.pageTitle}>
              {showPatientSelector
                ? t('Dialogs:INVOICE_DIALOG.TITLE')
                : formatInvoiceHeaderSubtitle(
                    invoiceType,
                    invoiceNoSuffix,
                    clientName,
                    patientName,
                  )}
            </h2>
          )}
          <KibGrid className={styles.metaGrid} valign="center">
            <KibGridItem>
              <div className={styles.inlineRow}>
                <div className={styles.chipRow}>
                  <ClientWarningChip
                    className={styles.chip}
                    clientId={clientId}
                  />
                  <ClientPreferencesChip
                    className={styles.chip}
                    clientId={clientId}
                  />
                  <LinkedChewyAccountContainer
                    accountType={KyriosAccountType.CLIENT}
                    className={styles.chip}
                    clientId={clientId}
                  />
                  <BusinessShareIcon
                    noMargin
                    businessIds={client?.clientInContextBusinesses}
                    className={styles.chip}
                  />
                  <span className={styles.entityName}>{clientName}</span>
                </div>
                <ClientInfoLabel clientId={client?.id} variant="body2" />
              </div>
            </KibGridItem>
            <KibGridItem>
              <div className={styles.chipRow}>
                <PatientWarningChip
                  className={styles.chip}
                  patientId={patientId}
                />
                <PatientPreferencesChip
                  className={styles.chip}
                  patientId={patientId}
                />
                <LinkedChewyAccountContainer
                  accountType={KyriosAccountType.PATIENT}
                  className={styles.chip}
                  clientId={clientId}
                  patientId={patientId}
                />
                <WellnessPlanMemberButton
                  chip
                  readonly
                  buttonClassName={styles.chip}
                  className={styles.wellnessChip}
                  clientId={clientId}
                  patientId={patientId}
                />
                <span className={styles.entityName}>{patientName}</span>
                <PatientInfoLabel patientId={patient?.id} variant="body2" />
              </div>
            </KibGridItem>
          </KibGrid>
        </div>
        {showPatientSelector ? (
          <PatientSelector clientId={clientId} onSelected={onPatientSelected} />
        ) : isBatchInvoice ? (
          <BatchInvoice {...(invoiceProps as BatchInvoiceProps)} />
        ) : (
          <Invoice {...(invoiceProps as InvoiceProps)} />
        )}
      </KibContainer>
    )
  },
)

export default InvoiceBody
